import React, { useState } from "react";
import "leaflet/dist/leaflet.css";
import { InputNumber, Button, Input, Card, Typography, Tag } from "antd";
import { CheckOutlined, ClearOutlined } from "@ant-design/icons";
import { saveGeofenceToServer } from "mqtt/location";
import { TbPolygon, TbCircle } from "react-icons/tb";

const { Text } = Typography;

const FENCE_TYPES = [
  {
    value: "polygon",
    label: "Polygon",
    Icon: TbPolygon,
    color: "#7c3aed",
    bg: "#f5f3ff",
    border: "#c4b5fd",
    activeBorder: "#7c3aed",
  },
  {
    value: "circle",
    label: "Circle",
    Icon: TbCircle,
    color: "#f97316",
    bg: "#fff7ed",
    border: "#fed7aa",
    activeBorder: "#f97316",
  },
];

const FieldLabel = ({ children }) => (
  <Text
    style={{
      fontSize: 11, fontWeight: 600, color: "#64748b",
      textTransform: "uppercase", letterSpacing: "0.06em",
      display: "block", marginBottom: 6,
    }}
  >
    {children}
  </Text>
);

const CreateGeoFence = ({
  savedFences,
  setSavedFences,
  handlePanToView,
  polygonPoints,
  setPolygonPoints,
  fenceType,
  setFenceType,
  circleCenter,
  setCircleCenter,
  circleRadius,
  setCircleRadius,
  vehicle,
}) => {
  const [fenceName, setFenceName] = useState("");
  const [fenceDescription, setFenceDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const canSavePolygon = polygonPoints.length >= 3 && fenceName.trim().length > 0;
  const canSaveCircle = !!circleCenter && circleRadius > 0 && fenceName.trim().length > 0;
  const canSave = fenceType === "polygon" ? canSavePolygon : canSaveCircle;
  const canClear = fenceType === "polygon" ? polygonPoints.length > 0 : !!circleCenter;

  const handleClear = () => {
    if (fenceType === "polygon") setPolygonPoints([]);
    else setCircleCenter(null);
  };

  const saveFenceToServer = async (fence) => {
    const convertToPayload = (f) => {
      const common = {
        vehicleKey: vehicle?.vehicleKey,
        serialNo: vehicle?.serialNo,
        name: f.name,
        description: f.description || "",
      };
      if (f.type === "polygon") {
        const closing = { lat: f.points[0][0], lng: f.points[0][1] };
        return {
          type: "polygon",
          polygon: {
            coordinates: [
              ...f.points.map((p) => ({ lat: p[0], lng: p[1] })),
              closing,
            ],
          },
          ...common,
        };
      }
      return {
        type: "circle",
        circle: {
          center: { lat: f.center[0], lng: f.center[1] },
          radius: circleRadius,
        },
        ...common,
      };
    };

    const payload = convertToPayload(fence);
    console.log("Saving fence:", payload);
    const saved = await saveGeofenceToServer(payload);
    console.log("Saved:", saved);
    setSavedFences((prev) => [...prev, saved]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const fence =
        fenceType === "polygon"
          ? { type: "polygon", points: polygonPoints, name: fenceName, description: fenceDescription }
          : { type: "circle", center: circleCenter, radius: circleRadius, name: fenceName, description: fenceDescription };
      await saveFenceToServer(fence);
      // reset form
      if (fenceType === "polygon") setPolygonPoints([]);
      else setCircleCenter(null);
      setFenceName("");
      setFenceDescription("");
    } catch (err) {
      console.error("Error saving fence:", err);
    } finally {
      setSaving(false);
    }
  };

  const hintText =
    fenceType === "polygon"
      ? polygonPoints.length > 0
        ? `${polygonPoints.length} point${polygonPoints.length !== 1 ? "s" : ""} placed${polygonPoints.length < 3 ? ` — ${3 - polygonPoints.length} more needed` : " — ready to save"}`
        : "Click on the map to start placing polygon points"
      : circleCenter
        ? "Center placed — set the radius and save"
        : "Click on the map to place the circle center";

  const activeCfg = FENCE_TYPES.find((t) => t.value === fenceType);

  return (
    <Card
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 28, height: 28, borderRadius: 7,
              background: activeCfg.bg, border: `1px solid ${activeCfg.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: activeCfg.color, fontSize: 16,
            }}
          >
            <activeCfg.Icon />
          </div>
          <span>Create Zone</span>
        </div>
      }
      style={{ borderRadius: 12, border: "1px solid #e2e8f0", width: "100%" }}
      styles={{ body: { display: "flex", flexDirection: "column", gap: 16 } }}
    >

      <div>
        <FieldLabel>Fence Type</FieldLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {FENCE_TYPES.map((opt) => {
            const active = fenceType === opt.value;
            return (
              <div
                key={opt.value}
                onClick={() => setFenceType(opt.value)}
                style={{
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: `1.5px solid ${active ? opt.activeBorder : "#e2e8f0"}`,
                  background: active ? opt.bg : "#f8fafc",
                  cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 8,
                  transition: "all 0.15s",
                  userSelect: "none",
                }}
              >
                <div style={{ fontSize: 20, color: active ? opt.color : "#94a3b8", lineHeight: 1 }}>
                  <opt.Icon />
                </div>
                <span
                  style={{
                    fontSize: 12, fontWeight: 700,
                    color: active ? opt.color : "#64748b",
                  }}
                >
                  {opt.label}
                </span>
                {active && (
                  <div style={{ marginLeft: "auto" }}>
                    <CheckOutlined style={{ fontSize: 11, color: opt.color }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div
        style={{
          padding: "8px 12px",
          borderRadius: 8,
          background: activeCfg.bg,
          border: `1px solid ${activeCfg.border}`,
        }}
      >
        <Text style={{ fontSize: 11, color: activeCfg.color, lineHeight: 1.5 }}>
          {hintText}
        </Text>
        {fenceType === "polygon" && polygonPoints.length >= 3 && (
          <Tag
            style={{
              marginLeft: 6, borderRadius: 20, fontSize: 9,
              background: "#dcfce7", border: "1px solid #86efac", color: "#16a34a",
            }}
          >
            ✓ Min reached
          </Tag>
        )}
      </div>

      <div>
        <FieldLabel>Zone Name *</FieldLabel>
        <Input
          placeholder="e.g. Office Campus"
          value={fenceName}
          onChange={(e) => setFenceName(e.target.value)}
          status={fenceName.trim().length === 0 ? "warning" : ""}
        />
      </div>

      <div>
        <FieldLabel>Description</FieldLabel>
        <Input
          placeholder="Optional description..."
          value={fenceDescription}
          onChange={(e) => setFenceDescription(e.target.value)}
        />
      </div>

      {fenceType === "circle" && (
        <div>
          <FieldLabel>Radius</FieldLabel>
          <InputNumber
            min={10}
            max={50000}
            value={circleRadius}
            onChange={setCircleRadius}
            style={{ width: "100%" }}
            addonAfter={<span style={{ fontSize: 11, color: "#64748b" }}>meters</span>}
          />
        </div>
      )}

      {fenceType === "polygon" && polygonPoints.length > 0 && (
        <div
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "8px 12px", borderRadius: 8,
            background: "#f5f3ff", border: "1px solid #c4b5fd",
          }}
        >
          <Text style={{ fontSize: 12, color: "#7c3aed" }}>
            <strong>{polygonPoints.length}</strong> point{polygonPoints.length !== 1 ? "s" : ""} placed
          </Text>
          {polygonPoints.length < 3 && (
            <Text style={{ fontSize: 11, color: "#a78bfa" }}>
              {3 - polygonPoints.length} more needed
            </Text>
          )}
          {polygonPoints.length >= 3 && (
            <Tag style={{ margin: 0, borderRadius: 20, background: "#dcfce7", border: "1px solid #86efac", color: "#16a34a", fontSize: 10 }}>
              Ready
            </Tag>
          )}
        </div>
      )}

      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
        <Button
          type="primary"
          block
          size="large"
          loading={saving}
          disabled={!canSave}
          onClick={handleSave}
          style={{
            background: canSave ? activeCfg.color : undefined,
            borderColor: canSave ? activeCfg.color : undefined,
          }}
          icon={<CheckOutlined />}
        >
          Save {fenceType === "polygon" ? "Polygon" : "Circle"} Zone
        </Button>
        <Button
          block
          disabled={!canClear}
          onClick={handleClear}
          icon={<ClearOutlined />}
        >
          Clear Drawing
        </Button>
      </div>
    </Card>
  );
};

export default CreateGeoFence;
